import React from "react";
import { Switch, Button } from "antd";
import { CheckSquareOutlined, CloseOutlined } from "@ant-design/icons";
import { render } from "@testing-library/react";

// Improved helper function to robustly parse price values for sorting
function parsePrice(val) {
  if (!val || val === "0" || val === 0) return 0;
  if (typeof val !== 'string') return 0;
  const num = parseFloat(val.replace(/[^0-9.]/g, ""));
  return isNaN(num) ? 0 : num;
}

const columns = [ 
  {
    title: "BC Product (SKU)",
    dataIndex: "bcSku",
    sorter: (a, b) => a.bcSku.localeCompare(b.bcSku),
    width: 200,
    ellipsis: true,
    render: (_, record) => <Button type="link"></Button>,
    fixed: 'left',
  },
  {
    title: "BC Product Name",
    dataIndex: "bc_name",
    // sorter: (a, b) => (a.bc_name || '').localeCompare(b.bc_name || ''),
    width: 200,
    ellipsis: true,
    onCell: () => ({
      className: 'bc-product-name-cell',
    }),
  },
   {
    title: "Active on Store",
    dataIndex: "active",
    render: (_, record) => <Switch checked={record.active} />,
    sorter: (a, b) => (a.active === b.active ? 0 : a.active ? 1 : -1),
    width: 150,
    ellipsis: true,
  },
  {
    title: "SAP SKU",
    dataIndex: "sapSku",
    sorter: (a, b) => a.sapSku.localeCompare(b.sapSku),
    width: 110,
    ellipsis: true,
  },
  {
    title: "SAP Name",
    dataIndex: "sapName",
    sorter: (a, b) => (a.sapName || '').localeCompare(b.sapName || ''),
    width: 200
  },
  {
    title: "SAP Customer Number",
    dataIndex: "sapCustomerNumber",
    sorter: (a, b) => a.sapCustomerNumber.localeCompare(b.sapCustomerNumber),
    width: 200,
    ellipsis: true,
  },
  {
    title: "SAP Price",
    dataIndex: "sapPrice",
    sorter: (a, b) => parseFloat(a.sapPrice.slice(1)) - parseFloat(b.sapPrice.slice(1)),
    width: 110,
    ellipsis: true,
  },
  {
    title: "SAP MSRP",
    dataIndex: "sapMsrp",
    sorter: (a, b) => parseFloat(a.sapMsrp.slice(1)) - parseFloat(b.sapMsrp.slice(1)),
    width: 120,
    ellipsis: true,
  },
  {
    title: "BC MSRP",
    dataIndex: "msrp",
    width: 130,
    sorter: (a, b) => {
      const aMsrp = parsePrice(a.prdouct_data?.prod_msrp);
      const bMsrp = parsePrice(b.prdouct_data?.prod_msrp);
      if (aMsrp === 0 && bMsrp === 0) return 0;
      if (aMsrp === 0) return 1;
      if (bMsrp === 0) return -1;
      return aMsrp - bMsrp;
    },
    render: (_, record) => {
      const msrp = record.prdouct_data?.prod_msrp;
      if (msrp && msrp !== "0") {
        return record.currency_symbol + msrp;
      }
      return "NA";
    },
  },
  {
    title: "BC Sale Price",
    dataIndex: "sale_price",
    width: 130,
    sorter: (a, b) => {
      const aSale = parsePrice(a.prdouct_data?.prod_sale_price);
      const bSale = parsePrice(b.prdouct_data?.prod_sale_price);
      if (aSale === 0 && bSale === 0) return 0;
      if (aSale === 0) return 1;
      if (bSale === 0) return -1;
      return aSale - bSale;
    },
    render: (_, record) => {
      const salePrice = record.prdouct_data?.prod_sale_price;
      if (salePrice && salePrice !== "0") {
        return record.currency_symbol + salePrice;
      }
      return "NA";
    },
  },
  {
    title: "Available Stock",
    dataIndex: "availableStock",
    sorter: (a, b) => a.availableStock - b.availableStock,
    width: 150,
    ellipsis: true,
    // onCell: (record) => ({
    //   style: {
    //     backgroundColor: parseFloat(record.sapMsrp.replace(/[^0-9.]/g, "")) == 0 ? "rgba(255, 0, 0, 0.2)" : "transparent",
    //   },
    // }),
  },
  {
    title: "Reserved Stock",
    dataIndex: "reservedStock",
    sorter: (a, b) => a.reservedStock - b.reservedStock,
    width: 150,
    ellipsis: true,
  },

  {
    title: "Type",
    dataIndex: "type",
    sorter: (a, b) => a.type.localeCompare(b.type),
    width: 100,
    ellipsis: true,
  },
 
  {
    title: "Store",
    dataIndex: "store",
    sorter: (a, b) => a.store.localeCompare(b.store),
    width: 100,
    ellipsis: true,
  },
  {
    title: "BC Inventory",
    dataIndex: "bcInventory",
    sorter: (a, b) => a.bcInventory - b.bcInventory,
    width: 150,
    ellipsis: true,
  },
  {
    title: "Subscription",
    dataIndex: "subscription",
    sorter: (a, b) => a.subscription.localeCompare(b.subscription),
    width: 150,
    ellipsis: true,
  },
  {
    title: "Enrichment",
    dataIndex: "enrichment",
    render: (text) =>
      text ? (
        <CheckSquareOutlined style={{ color: "green" }} />
      ) : (
        <CloseOutlined style={{ color: "red" }} />
      ),
    sorter: (a, b) => (a.enrichment === b.enrichment ? 0 : a.enrichment ? 1 : -1),
    width: 150,
    ellipsis: true,
  },
  {
    title: "Last Updated",
    dataIndex: "lastUpdated",
    sorter: (a, b) => {
      function parseDMY(dateStr) {
        if (!dateStr) return 0;
        const [datePart, timePart] = dateStr.split(', ');
        if (!datePart || !timePart) return 0;
        const [day, month, year] = datePart.split('/').map(Number);
        const [hour, minute, second] = timePart.split(':').map(Number);
        return new Date(year, month - 1, day, hour, minute, second).getTime();
      }
      return parseDMY(a.lastUpdated) - parseDMY(b.lastUpdated);
    },
    width: 200,
    ellipsis: true,
  }, 
];

export default columns;
